import React, { createContext, useContext } from 'react';
import { useSystemAirflow } from '../hooks/useSystemAirflow';
import SystemAirflowSection, {
  ClipAirflow,
  PeriodAirflowValue,
  PeriodCfmPerTon,
  isSystemAirflowV2,
} from './DeviceWorkspace/SystemAirflowSection';
import FilterConfiguration from './DeviceWorkspace/FilterConfiguration';
import { PeriodAirflowRecord } from '../types';

export const CURRENT_SYSTEM_HEADING = 'Current system, clean-filter estimate';
export const AIRFLOW_HEADING = 'Airflow';
export const PAST_PERIOD_AIRFLOW = 'Airflow is shown for the current filter only.';
export const NO_PERIOD_AIRFLOW = 'No airflow record for this filter period.';
export const CURRENT_FILTER_CONFIGURATION_HEADING = 'Current system, filter configuration';

type SystemAirflowState = ReturnType<typeof useSystemAirflow>;

/** One system-airflow read per device on the page, keyed by device id. */
const SystemAirflowCurrentContext = createContext<Readonly<Record<number, SystemAirflowState>>>({});

const DeviceSystemAirflow: React.FC<{ deviceId: number; children: React.ReactNode }> = ({
  deviceId,
  children,
}) => {
  const outer = useContext(SystemAirflowCurrentContext);
  const state = useSystemAirflow(deviceId);
  return (
    <SystemAirflowCurrentContext.Provider value={{ ...outer, [deviceId]: state }}>
      {children}
    </SystemAirflowCurrentContext.Provider>
  );
};

const Chain: React.FC<{ deviceIds: number[]; children: React.ReactNode }> = ({ deviceIds, children }) => {
  if (deviceIds.length === 0) return <>{children}</>;
  const [first, ...rest] = deviceIds;
  return (
    <DeviceSystemAirflow deviceId={first}>
      <Chain deviceIds={rest}>{children}</Chain>
    </DeviceSystemAirflow>
  );
};

/**
 * FS-593 — reads GET /devices/:id/system-airflow once per device and hands
 * the result to both Analysis-page blocks below, which sit in different
 * sections of the page (the filter configuration under Filter Info, the
 * clean-filter estimate in the Airflow section). One hook call per device,
 * however many blocks or pinned analyses show it.
 */
export const SystemAirflowCurrentProvider: React.FC<{
  deviceIds: number[];
  children: React.ReactNode;
}> = ({ deviceIds, children }) => (
  <Chain deviceIds={Array.from(new Set(deviceIds))}>{children}</Chain>
);

/**
 * The read for this device, or null when it is not an FS-566 response. The
 * Analysis page never carried these blocks before FS-566, so an older
 * response (or none yet) renders nothing.
 */
const useCurrentV2 = (deviceId: number) => {
  const state = useContext(SystemAirflowCurrentContext)[deviceId];
  if (!state?.data || !isSystemAirflowV2(state.data)) return null;
  return { ...state, data: state.data };
};

/**
 * FS-566 — the Overview's system airflow block, on the Analysis page. It is
 * always the CURRENT filter period, so it is labeled as such rather than read
 * as a summary of the dates the page shows.
 */
export const SystemAirflowCurrentEstimate: React.FC<{ deviceId: number }> = ({ deviceId }) => {
  const current = useCurrentV2(deviceId);
  if (!current) return null;
  return (
    <SystemAirflowSection
      data={current.data}
      deviceId={deviceId}
      heading={CURRENT_SYSTEM_HEADING}
      headingTitle={
        "This block is the system's clean-filter estimate for the current filter period, " +
        'whatever dates the page shows.'
      }
    />
  );
};

/**
 * FS-748 (Plan 127 §5a) — the Analysis page's Airflow section, which replaced
 * the daily airflow chart. Airflow is ONE value per filter period: the
 * Clip's clean-filter estimate from /system-airflow, the same number as the
 * Overview, with its calibration beside it. On a system of several Clips this
 * Clip's value comes first and the system total and CFM per ton stay in the
 * block beneath; on a one-Clip system that block IS this Clip's value.
 *
 * Plan 127 Phase 3: a previous filter period shows its stored record
 * (`periodAirflow`, from the analysis response), laid out like the current
 * value, or says it has none. A response from a Lambda before Phase 3 has no
 * such field, and the page then says airflow is for the current filter only,
 * as before. The current period always reads /system-airflow, even when the
 * analysis response carries a record for it: one number, the Overview's.
 * `showHeading` is false where the page already titles the section (several
 * devices side by side).
 *
 * FS-878: under a previous period's value, its CFM per ton using today's
 * tonnage on a one-Clip system, from the same /system-airflow read (today's
 * tonnage and Clips); a note on a system of several. Nothing more until that
 * read answers.
 */
export const AnalysisAirflow: React.FC<{
  deviceId: number;
  pastPeriod: boolean;
  /** The selected period's record: undefined from an older Lambda, null when there is none. */
  periodAirflow?: PeriodAirflowRecord | null;
  showHeading: boolean;
}> = ({ deviceId, pastPeriod, periodAirflow, showHeading }) => {
  const current = useCurrentV2(deviceId);
  const heading = showHeading && (
    <h3 className="text-sm font-semibold text-gray-700 mb-2">{AIRFLOW_HEADING}</h3>
  );
  if (pastPeriod && periodAirflow) {
    return (
      <div data-testid="analysis-airflow">
        {heading}
        <PeriodAirflowValue record={periodAirflow} />
        {current && <PeriodCfmPerTon record={periodAirflow} data={current.data} />}
      </div>
    );
  }
  if (pastPeriod && periodAirflow === null) {
    return (
      <div data-testid="analysis-airflow">
        {heading}
        <p
          className="text-sm text-gray-600"
          title="A filter period's airflow is stored once its clean days are in; this period has no stored value."
        >
          {NO_PERIOD_AIRFLOW}
        </p>
      </div>
    );
  }
  if (pastPeriod) {
    return (
      <div data-testid="analysis-airflow">
        {heading}
        <p
          className="text-sm text-gray-600"
          title="Per-period airflow values are not available yet; the current filter's value is on the current period and the Overview."
        >
          {PAST_PERIOD_AIRFLOW}
        </p>
      </div>
    );
  }
  if (!current) return null;
  const clips = Array.isArray(current.data.clips) ? current.data.clips : [];
  const self = clips.find((clip) => clip.device_id === deviceId);
  const several = clips.length + (current.data.hidden_count ?? 0) >= 2;
  return (
    <div data-testid="analysis-airflow">
      {heading}
      {several && self && <ClipAirflow clip={self} />}
      <SystemAirflowCurrentEstimate deviceId={deviceId} />
    </div>
  );
};

/**
 * FS-593 — the Overview's filter configuration, directly under the Analysis
 * page's Filter Info, labeled as the current system because the page may be
 * showing an earlier period. Same rule as the estimate: only against an
 * FS-566 API, so a page served by an older one is exactly what it was.
 */
export const SystemAirflowCurrentConfiguration: React.FC<{ deviceId: number }> = ({ deviceId }) => {
  const current = useCurrentV2(deviceId);
  if (!current) return null;
  return (
    <FilterConfiguration
      data={current.data}
      loading={current.loading}
      error={current.error}
      deviceId={deviceId}
      heading={CURRENT_FILTER_CONFIGURATION_HEADING}
      headingTitle={
        "Current filter records for this system, from the app. They describe the filters today, whatever period this page shows."
      }
      refresh={current.refresh}
      onRefresh={current.refetch}
    />
  );
};
